import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Dumbbell,
  Search,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const muscleGroups = [
  "Pecho",
  "Espalda",
  "Hombros",
  "Bíceps",
  "Tríceps",
  "Piernas",
  "Glúteos",
  "Core",
];

const equipmentOptions = [
  "Máquina",
  "Polea",
  "Barra",
  "Mancuernas",
  "Barra/Mancuernas",
  "Barra T",
  "Smith",
  "Peso corporal",
  "Mancuerna/Smith",
];

const AdminExercises = () => {
  const { getAuthHeaders } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [muscleFilter, setMuscleFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    equipment: "",
    muscle_group: "",
    tips: "",
    description: "",
    video_url: "",
  });

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/exercises`, {
        headers: getAuthHeaders(),
      });
      setExercises(response.data);
    } catch (error) {
      console.error("Error fetching exercises:", error);
      toast.error("Error al cargar ejercicios");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      equipment: "",
      muscle_group: "",
      tips: "",
      description: "",
      video_url: "",
    });
    setEditingExercise(null);
  };

  const openEditDialog = (exercise) => {
    setEditingExercise(exercise);
    setFormData({
      name: exercise.name,
      equipment: exercise.equipment,
      muscle_group: exercise.muscle_group,
      tips: exercise.tips || "",
      description: exercise.description || "",
      video_url: exercise.video_url || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.equipment || !formData.muscle_group) {
      toast.error("Por favor completa los campos obligatorios");
      return;
    }

    setSaving(true);
    try {
      if (editingExercise) {
        await axios.put(
          `${API_URL}/api/admin/exercises/${editingExercise.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Ejercicio actualizado");
      } else {
        await axios.post(
          `${API_URL}/api/admin/exercises`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Ejercicio creado");
      }
      
      fetchExercises();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving exercise:", error);
      toast.error("Error al guardar el ejercicio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (exerciseId) => {
    if (!window.confirm("¿Estás seguro de eliminar este ejercicio?")) return;
    
    setDeleting(exerciseId);
    try {
      await axios.delete(`${API_URL}/api/admin/exercises/${exerciseId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Ejercicio eliminado");
      fetchExercises();
    } catch (error) {
      console.error("Error deleting exercise:", error);
      toast.error("Error al eliminar el ejercicio");
    } finally {
      setDeleting(null);
    }
  };

  const filteredExercises = exercises.filter((ex) => {
    const matchesSearch = ex.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ex.muscle_group.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesMuscle = muscleFilter === "all" || ex.muscle_group === muscleFilter;
    return matchesSearch && matchesMuscle;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-exercises">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Ejercicios</h1>
          <p className="text-muted-foreground mt-1">Gestiona los ejercicios del gimnasio</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold shadow-lg shadow-[#D4AF37]/20" data-testid="add-exercise-button">
              <Plus className="w-5 h-5 mr-2" />
              Agregar Ejercicio
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-heading text-xl">
                {editingExercise ? "Editar Ejercicio" : "Nuevo Ejercicio"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white">Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                  placeholder="Ej: Lat Pulldown"
                  data-testid="exercise-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Grupo Muscular *</Label>
                  <Select
                    value={formData.muscle_group}
                    onValueChange={(value) => setFormData({ ...formData, muscle_group: value })}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="exercise-muscle-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                      {muscleGroups.map((group) => (
                        <SelectItem key={group} value={group} className="text-white hover:bg-[#1A1A1A]">
                          {group}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Equipo *</Label>
                  <Select
                    value={formData.equipment}
                    onValueChange={(value) => setFormData({ ...formData, equipment: value })}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="exercise-equipment-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                      {equipmentOptions.map((eq) => (
                        <SelectItem key={eq} value={eq} className="text-white hover:bg-[#1A1A1A]">
                          {eq}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Consejos</Label>
                <Input
                  value={formData.tips}
                  onChange={(e) => setFormData({ ...formData, tips: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                  placeholder="Ej: Mantén los codos fijos"
                  data-testid="exercise-tips-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white min-h-[80px]"
                  placeholder="Descripción detallada del ejercicio"
                  data-testid="exercise-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">URL de Video (opcional)</Label>
                <Input
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                  placeholder="https://youtube.com/..."
                  data-testid="exercise-video-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
                  data-testid="save-exercise-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    editingExercise ? "Actualizar" : "Crear"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar ejercicios..."
            className="pl-10 bg-secondary/50 border-transparent focus:border-primary text-white"
            data-testid="search-exercises-input"
          />
        </div>
        <Select value={muscleFilter} onValueChange={setMuscleFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="muscle-filter-select">
            <SelectValue placeholder="Filtrar por músculo" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#2A2A2A]">
            <SelectItem value="all" className="text-white hover:bg-[#1A1A1A]">
              Todos los músculos
            </SelectItem>
            {muscleGroups.map((group) => (
              <SelectItem key={group} value={group} className="text-white hover:bg-[#1A1A1A]">
                {group}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>{filteredExercises.length} ejercicios</span>
        {muscleFilter !== "all" && (
          <Badge variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37]">
            {muscleFilter}
            <button 
              onClick={() => setMuscleFilter("all")} 
              className="ml-2 hover:text-white"
            >
              ×
            </button>
          </Badge>
        )}
      </div>

      {/* Table */}
      <Card className="bg-[#121212] border-[#2A2A2A]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            </div>
          ) : filteredExercises.length === 0 ? (
            <div className="p-8 text-center">
              <Dumbbell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No se encontraron ejercicios</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Nombre</TableHead>
                    <TableHead className="text-muted-foreground">Músculo</TableHead>
                    <TableHead className="text-muted-foreground">Equipo</TableHead>
                    <TableHead className="text-muted-foreground text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExercises.map((exercise) => (
                    <TableRow
                      key={exercise.id}
                      className="border-white/5 hover:bg-[#1A1A1A]"
                      data-testid={`exercise-row-${exercise.id}`}
                    >
                      <TableCell className="font-medium text-white">
                        {exercise.name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37]">
                          {exercise.muscle_group}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {exercise.equipment}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(exercise)}
                            className="hover:bg-[#1A1A1A]"
                            data-testid={`edit-exercise-${exercise.id}`}
                          >
                            <Pencil className="w-4 h-4 text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(exercise.id)}
                            disabled={deleting === exercise.id}
                            className="hover:bg-destructive/20"
                            data-testid={`delete-exercise-${exercise.id}`}
                          >
                            {deleting === exercise.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-destructive" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminExercises;
